'use client';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';

import { fNumber } from 'src/utils/format/format-number';

import { SEGMENT } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import { PlatformIcon } from 'src/components/platform-icon/platform-icon';

/**
 * **탭 줄 정본** — 알약 하나 안에서 자리를 옮기는 꼴. 이 서비스의 탭은 전부 이것이다.
 *
 * 🚨 **탭을 새로 그리지 마라** — MUI `Tabs`·밑줄 탭·`ButtonGroup`·테두리 칩(`HubChip`)도 아니다.
 *   모양을 바꿀 일이 있으면 여기와 `SEGMENT`(`theme/ui-tokens`) 한 곳만 고친다.
 * 🔑 **보기를 바꾸면 갈래 수와 상관없이 이것**이다(둘이어도) — 면 나누기(개요 · 포스팅)도, 보는 것을
 *   갈아 끼우는 고르개(지표 · 순위 기준 · 그래프/표)도. 조건을 거는 것(필터)만 칩이다.
 * 🚨 **제 너비만 쓴다**(`alignSelf: flex-start`) — 세로 `Stack` 의 기본 «늘리기»면 띠가 화면 폭이 된다.
 * 🚨 **한 줄이다 — 접지 않는다.** 본문 위 탭은 넘치면 옆으로 구르고, 폭이 정해진 자리(대화상자·
 *   서랍·좁은 카드)에서 갈래가 셋 이하면 `fullWidth` 로 고르게 나눈다 — 칸 안 글자는 말줄임(…).
 *   ⚠️ 창 안에서 구르게 두면 끝 칸이 잘려 «없는 탭»처럼 보인다. 폰에서는 그림을 빼 자리를 번다.
 *   ⚠️ 말줄임은 마지막 수단이다 — 폰에서 잘리는 이름은 `shortLabel`(「우리 브랜드」)을 준다.
 * 🔑 `hint` 는 알약에 마우스를 올리면 뜨는 한 줄 — 갈래의 뜻을 이름만으로 못 담을 때.
 *   `disabled` 칸은 자리를 남기고 흐려진다(눌리지 않는다) — 빼면 왜 없는지 알 길이 없다.
 * ⛔ **조회해서 세는 수는 탭 이름 뒤에 붙이지 마라.** 아직 안 온 수는 0 이라 이름이 흔들리고, 면마다
 *   세는 단위가 달라 견줄 수 있는 것처럼 보인다. 그런 수는 **면을 연 뒤** 그 면의 딸린 말이 적는다.
 * 🔑 `count` 는 **내가 담아 둔 것의 개수**처럼 이미 손에 쥔 수에만 쓴다(「즐겨찾기 12」) — 이름 뒤에
 *   흐린 숫자로 선다. 모르는 동안은 `undefined` 로 넘겨 아무것도 안 세우고, 0 이어도 안 세운다(`showZero` 를 켠 칸만 0 을 세운다).
 *   수를 세는 중이면 `countLoading` 으로 그 자리에 작은 스피너를 세운다(수보다 먼저 본다).
 */
export function SegmentTabs<T extends string | number>({
  value,
  onChange,
  tabs,
  fullWidth = false,
  size = 'medium',
}: {
  value: T;
  onChange: (next: T) => void;
  /** 이름 앞 그림 — `Iconify` 이름. 없으면 글자만 선다 */
  tabs: {
    key: T;
    label: string;
    /** 폰(sm 미만)에서 대신 서는 짧은 이름 — 고르게 나눈 칸에서 말줄임이 생길 때 */
    shortLabel?: string;
    icon?: string;
    /** 채널 탭이면 채널 원값(`instagram`·`threads` …) — `icon` 대신 서비스 채널 아이콘(`PlatformIcon`)이 선다 */
    platformId?: string;
    count?: number;
    /** 켜면 `count` 가 0 이어도 「0」을 세운다 — `undefined` 는 여전히 아무것도 안 세운다 */
    showZero?: boolean;
    /** 켜면 수 자리에 작은 스피너 — 수를 받는 중일 때 */
    countLoading?: boolean;
    hint?: string;
    disabled?: boolean;
  }[];
  /**
   * 폭을 꽉 채우고 칸을 고르게 나눈다(구르지 않고, 넘치는 글자는 칸 안에서 말줄임) —
   * 대화상자·서랍·좁은 카드처럼 **컨테이너 폭이 정해진 자리 + 갈래 셋 이하**면 기본으로 켠다.
   * ⚠️ 본문 위에 얹히는 탭은 켜지 마라 — 폭이 넓으면 알약이 띠로 늘어져 탭으로 안 읽힌다.
   */
  fullWidth?: boolean;
  /** `small` — 칸 머리 옆처럼 좁은 자리의 작은 알약(높이 24 · 메타 글자) */
  size?: 'small' | 'medium';
}) {
  const small = size === 'small';
  return (
    <Stack
      direction="row"
      spacing={0.5}
      useFlexGap
      sx={{
        ...SEGMENT.shell,
        ...(small ? { p: 0.25 } : null),
        maxWidth: '100%',
        ...(fullWidth ? { display: 'flex', alignSelf: 'stretch', width: '100%' } : null),
        // 어느 폭에서나 한 줄 — 고르게 나눈 띠는 칸이 줄고, 아니면 넘치는 만큼 옆으로 구른다
        flexWrap: 'nowrap',
        overflowX: fullWidth ? 'hidden' : 'auto',
        // 구르되 막대는 안 보인다 — 알약 줄에 가로 막대가 생기면 조작처럼 읽힌다
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {tabs.map((tab) => {
        const on = value === tab.key;
        const button = (
          <Button
            key={tab.key}
            size="small"
            disabled={tab.disabled}
            onClick={() => onChange(tab.key)}
            startIcon={
              tab.platformId ? (
                <PlatformIcon platformId={tab.platformId} size={14} />
              ) : tab.icon ? (
                <Iconify icon={tab.icon} width={14} />
              ) : undefined
            }
            sx={{
              height: small ? 24 : 28,
              minWidth: 0,
              px: fullWidth || small ? 1 : 1.5,
              flexShrink: 0,
              // 고르게 나눈 칸은 글자 길이와 상관없이 같은 폭 — 넘치는 글자는 칸 안에서 말줄임
              ...(fullWidth ? { flex: '1 1 0' } : null),
              typography: small ? 'uiMeta' : 'uiAction',
              whiteSpace: 'nowrap',
              // 폰에서는 그림을 뺀다 — 구르기 전에 한 칸이라도 더 보이게
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'inline-flex' }, mr: 0.5, ml: 0 },
              ...SEGMENT.item(on),
            }}
          >
            <Box
              component="span"
              sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', ...(tab.shortLabel ? { display: { xs: 'none', sm: 'inline' } } : null) }}
            >
              {tab.label}
            </Box>
            {tab.shortLabel && (
              <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: { xs: 'inline', sm: 'none' } }}>
                {tab.shortLabel}
              </Box>
            )}
            {tab.countLoading && (
              <CircularProgress size={10} thickness={5} aria-label="세는 중" sx={{ ml: 0.5, color: 'text.disabled' }} />
            )}
            {!tab.countLoading && (tab.showZero ? tab.count !== undefined : !!tab.count) && (
              <Box
                component="span"
                sx={{ ml: 0.5, color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}
              >
                {fNumber(tab.count ?? 0)}
              </Box>
            )}
          </Button>
        );
        if (!tab.hint) return button;
        return (
          <Tooltip key={tab.key} title={tab.hint} placement="top" disableInteractive>
            {/* 막힌 단추는 마우스 신호를 안 내 툴팁이 못 뜬다 — 껍데기로 감싼다 */}
            {tab.disabled ? (
              <Box component="span" sx={{ display: 'inline-flex', minWidth: 0, ...(fullWidth ? { flex: '1 1 0', '& > *': { flex: 1 } } : { flexShrink: 0 }) }}>
                {button}
              </Box>
            ) : button}
          </Tooltip>
        );
      })}
    </Stack>
  );
}
